import JsSIP from 'jssip';
import VoiceCallsApiClient from '../api/VoiceCallsApiClient';
import { VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID } from '../config/voiceWebRtcConfig';
import getOrCreateVoiceTabSessionId, { rotateVoiceTabSessionId } from './voiceTabSession';
import {
  AGENT_ANSWER_ABORTED,
  canTreatAgentIceAsPostAnswer,
  emitSdkEvent,
  getCurrentAgentCall,
  getVoiceAgentState,
  isAgentClientConnected,
  subscribeAgentSdkEvents
} from './voiceAgentSessionStore';
import { releaseAgentIcePrewarmPeerConnection, prewarmAgentIceTransport } from './voiceIcePrewarm';
import {
  acquireLocalAudioStream,
  prefetchAgentLocalAudioStream,
  releaseAgentLocalAudioCapture
} from './voiceLocalAudioCapture';
import {
  attachPeerConnectionDiagnostics,
  attachRemoteAudio,
  playRemoteMediaElement,
  releaseRemoteAudioElement,
  resetRemoteAudioAttachmentTracking,
  scheduleAgentRemoteAudioPlayback,
  syncAgentRemoteAudioPlayback,
  unlockAgentRemoteAudio,
  logCurrentAgentRtcAudioStats
} from './voiceRemotePlayback';
import {
  isAgentCallDead as isRtcSessionDead,
  isAgentSessionAlreadyAnswered,
  isAgentSessionWaitingForAnswer,
  isPeerConnectionDead,
  isPeerConnectionIceReady,
  isPeerConnectionMediaReady,
  isTerminalSessionState
} from './voiceJsSipSessionStatus';
import {
  buildAgentPeerConnectionConfig,
  buildJsSipUaConfiguration,
  describeSdpSummary,
  resolveAgentAnswerPeerConnectionConfig,
  summarizeDisconnectEvent,
  summarizeSdp
} from './voiceSipUaConfig';
import { teardownSipUserAgent } from './voiceSipUaTeardown';
import {
  startAgentIceMediaWatchdog,
  stopAgentIceMediaWatchdog,
  summarizeIceServersForLog,
  AGENT_WEBRTC_MEDIA_FAILED
} from './voiceIceMediaWatchdog';
import { refreshVoiceJoinRoom } from '../socket/initializeVoiceSocket';
import { logWebRtcEvent, logWebRtcWarn } from './voiceWebRtcLog';

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
/** FreeSWITCH returns 503 Maximum Calls when too many agent_* AORs are alive — cool down hard. */
const RECONNECT_503_MIN_DELAY_MS = 20_000;

/**
 * @param {string} message
 * @returns {boolean}
 */
function isSipMaximumCallsError(message) {
  const text = typeof message === 'string' ? message : '';
  return text.includes('503') || /Maximum Calls/i.test(text);
}

/**
 * Rotate tab_session only when the SIP AOR is likely dirty/zombie.
 * Never rotate on 503 Maximum Calls — that creates MORE agent_* users and worsens the limit.
 *
 * @param {string} message
 * @returns {boolean}
 */
function shouldRotateTabSessionAfterRegisterFailure(message) {
  if (isSipMaximumCallsError(message)) {
    return false;
  }
  // Connection Error / timeout: keep same tab id first; disconnect handler may rotate if WSS dies dirty.
  if (/Connection Error/i.test(message) || /timed out/i.test(message)) {
    return false;
  }
  return true;
}

export {
  AGENT_ANSWER_ABORTED,
  getCurrentAgentCall,
  isAgentClientConnected,
  subscribeAgentSdkEvents
};
export { isAgentSessionAlreadyAnswered, isTerminalSessionState };
export { prefetchAgentLocalAudioStream, releaseAgentLocalAudioCapture };
export { releaseAgentIcePrewarmPeerConnection };
export {
  logCurrentAgentRtcAudioStats,
  playRemoteMediaElement,
  scheduleAgentRemoteAudioPlayback,
  syncAgentRemoteAudioPlayback,
  unlockAgentRemoteAudio
};
export { AGENT_WEBRTC_MEDIA_FAILED, stopAgentIceMediaWatchdog };

/**
 * Wait until SIP UA is registered (sdk.ready). Queues answer races when INVITE arrives early.
 *
 * @param {{ timeoutMs?: number, reason?: string }} [options]
 * @returns {Promise<import('jssip').UA>}
 */
export async function waitForAgentClientReady(options = {}) {
  const timeoutMs = options.timeoutMs ?? 20_000;
  const reason = options.reason || 'wait-ready';

  if (isAgentClientConnected()) {
    return getVoiceAgentState().sipUserAgent;
  }

  const connectPromise = connectAgentWebRtcClient({ reason });

  if (isAgentClientConnected()) {
    return getVoiceAgentState().sipUserAgent;
  }

  await new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      reject(new Error('Timed out waiting for agent WebRTC leg'));
    }, timeoutMs);

    const unsubscribe = subscribeAgentSdkEvents((event) => {
      if (event.type === 'ready' && isAgentClientConnected()) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        resolve();
      }
    });

    connectPromise
      .then(() => {
        if (settled) return;
        if (isAgentClientConnected()) {
          settled = true;
          clearTimeout(timer);
          unsubscribe();
          resolve();
        }
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        reject(error);
      });
  });

  if (!isAgentClientConnected()) {
    throw new Error('Timed out waiting for agent WebRTC leg');
  }
  return getVoiceAgentState().sipUserAgent;
}

export function isAgentCallDead(session = getVoiceAgentState().currentRtcSession) {
  return isRtcSessionDead(session);
}

/**
 * Drops a closed/terminated JsSIP session so a new Answer can POST /answer.
 * @returns {boolean} true if a dead session was cleared
 */
export function discardDeadAgentCall() {
  const state = getVoiceAgentState();
  const session = state.currentRtcSession;
  if (!session || !isRtcSessionDead(session)) return false;
  logWebRtcEvent('sdk.discardDeadSession', {
    id: session.id,
    status: session.status,
    ice: session.connection?.iceConnectionState,
    conn: session.connection?.connectionState
  });
  try {
    session.terminate();
  } catch {
    // already dead
  }
  if (state.currentRtcSession === session) {
    state.currentRtcSession = null;
  }
  releaseRemoteAudioElement();
  return true;
}

function resolveAllPendingIncomingSessions(session) {
  const state = getVoiceAgentState();
  state.pendingIncomingResolvers.forEach((waiter) => {
    try {
      waiter.resolve(session);
    } catch (error) {
      logWebRtcWarn('Voice pending incoming resolver error:', error);
    }
  });
  state.pendingIncomingResolvers.clear();
}

export function abortPendingIncomingAgentCallWait(reason = AGENT_ANSWER_ABORTED) {
  const state = getVoiceAgentState();
  const error = reason instanceof Error ? reason : new Error(reason);
  error.code = AGENT_ANSWER_ABORTED;
  if (state.pendingIncomingResolvers.size === 0) {
    state.pendingAutoAnswerCallControlId = null;
    return;
  }
  logWebRtcEvent('sdk.incomingWaitAborted', { message: error.message });
  state.pendingIncomingResolvers.forEach((waiter) => {
    try {
      waiter.reject(error);
    } catch {
      // ignore
    }
  });
  state.pendingIncomingResolvers.clear();
  state.pendingAutoAnswerCallControlId = null;
}

/**
 * @param {string|null|undefined} callControlId
 * @param {{ ownedByAnswerBridge?: boolean }} [options]
 */
export function markAgentCallAnswerPending(callControlId, options = {}) {
  const state = getVoiceAgentState();
  const id = typeof callControlId === 'string' ? callControlId.trim() : null;
  state.pendingAutoAnswerCallControlId = id;
  if (options.ownedByAnswerBridge === true && id) {
    state.agentAnswerBridgeCallControlId = id;
  } else if (!id) {
    state.agentAnswerBridgeCallControlId = null;
  }
  logWebRtcEvent('sdk.markAutoAnswerPending', {
    callControlId: state.pendingAutoAnswerCallControlId,
    ownedByAnswerBridge: state.agentAnswerBridgeCallControlId === id
  });
}

/**
 * @param {string} callControlId
 * @returns {boolean}
 */
export function isAgentAnswerBridgeOwning(callControlId) {
  const id = typeof callControlId === 'string' ? callControlId.trim() : '';
  return Boolean(id && getVoiceAgentState().agentAnswerBridgeCallControlId === id);
}

export function clearAgentAnswerBridgeOwnership() {
  getVoiceAgentState().agentAnswerBridgeCallControlId = null;
}

/**
 * Outbound / agent_sip_invite: accept FreeSWITCH INVITE without a second UI click.
 *
 * @param {string} callControlId
 * @param {{ timeoutMs?: number }} [options]
 */
export async function autoAnswerAgentSipInvite(callControlId, options = {}) {
  const state = getVoiceAgentState();
  const id = typeof callControlId === 'string' ? callControlId.trim() : '';
  if (!id) {
    throw new Error('autoAnswerAgentSipInvite: missing callControlId');
  }

  markAgentCallAnswerPending(id);
  await waitForAgentClientReady({ reason: 'auto-answer', timeoutMs: options.timeoutMs ?? 20_000 });

  const existing = getCurrentAgentCall();
  if (
    existing?.direction === 'incoming'
    && !isTerminalSessionState(existing.status)
  ) {
    if (!isAgentSessionAlreadyAnswered(existing)) {
      await applyAgentSessionAnswer(existing);
    } else {
      playRemoteMediaElement(state.agentRemoteMediaElementId || VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    }
    return existing;
  }

  const incomingCallPromise = waitForIncomingAgentCall({
    timeoutMs: options.timeoutMs ?? 45_000
  });
  const session = await answerCurrentAgentCall({
    incomingCallPromise,
    timeoutMs: options.timeoutMs ?? 45_000
  });
  return session;
}

/**
 * @param {import('jssip').RTCSession} session
 * @returns {Promise<boolean>}
 */
async function applyAgentSessionAnswer(session) {
  const state = getVoiceAgentState();
  if (!session || isTerminalSessionState(session.status)) {
    return false;
  }

  const inFlight = state.agentSessionAnswerInFlight.get(session);
  if (inFlight) {
    return inFlight;
  }

  const run = async () => {
    if (isAgentSessionAlreadyAnswered(session)) {
      logWebRtcEvent('session.answerSkipped', {
        id: session.id,
        reason: 'already_answered',
        status: session.status
      });
      return true;
    }
    if (!isAgentSessionWaitingForAnswer(session)) {
      logWebRtcEvent('session.answerSkipped', {
        id: session.id,
        reason: 'not_waiting_for_answer',
        status: session.status
      });
      return false;
    }

    const remoteElementId = state.agentRemoteMediaElementId || VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID;
    attachRemoteAudio(session, remoteElementId);
    attachPeerConnectionDiagnostics(session);

    // Wait for TURN allocate from prewarm before creating the answer PC (Firefox is slow on cold relay).
    if (state.agentIcePrewarmPromise) {
      try {
        await Promise.race([
          state.agentIcePrewarmPromise,
          new Promise((resolve) => {
            setTimeout(resolve, 2_000);
          })
        ]);
      } catch {
        // pre-warm failure is logged in connect; answer still uses credentials pcConfig
      }
    }
    // Free the probe PC only after waiting — closing it on INVITE dropped allocate before answer.
    releaseAgentIcePrewarmPeerConnection();

    let localStream;
    try {
      localStream = await acquireLocalAudioStream();
    } catch (error) {
      logWebRtcEvent('session.getUserMediaFailed', {
        id: session.id,
        message: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }

    const answerOptions = {
      mediaStream: localStream,
      mediaConstraints: { audio: true, video: false }
    };
    if (state.agentPeerConnectionConfig) {
      answerOptions.pcConfig = resolveAgentAnswerPeerConnectionConfig(state.agentPeerConnectionConfig);
      logWebRtcEvent('session.answerPcConfig', {
        iceTransportPolicy: answerOptions.pcConfig?.iceTransportPolicy,
        iceServerCount: answerOptions.pcConfig?.iceServers?.length ?? 0,
        iceUrlCount: (answerOptions.pcConfig?.iceServers ?? []).reduce((count, entry) => {
          const urls = Array.isArray(entry.urls) ? entry.urls : [entry.urls];
          return count + urls.filter((url) => typeof url === 'string').length;
        }, 0),
        hasTcpTurn: (answerOptions.pcConfig?.iceServers ?? []).some((entry) => {
          const urls = Array.isArray(entry.urls) ? entry.urls : [entry.urls];
          return urls.some((url) => typeof url === 'string'
            && (/[?&;]transport=tcp\b/i.test(url) || /^turns:/i.test(url)));
        }),
        iceServers: summarizeIceServersForLog(answerOptions.pcConfig?.iceServers)
      });
      if (!answerOptions.pcConfig?.iceServers?.length) {
        logWebRtcWarn(
          'session.answerPcConfig has no iceServers — Firefox/Chrome may stay in ICE checking with zero RTP'
        );
      }
    } else {
      logWebRtcWarn('session.answer without agentPeerConnectionConfig — TURN relay may be missing');
    }
    state.agentSessionAnswerInitiated.add(session);
    session.answer(answerOptions);
    state.currentRtcSession = session;
    try {
      session.unmute({ audio: true });
    } catch {
      // ignore
    }
    localStream.getAudioTracks().forEach((track) => {
      const audioTrack = track;
      audioTrack.enabled = true;
    });

    // JsSIP addTrack is async after answer(); only log — do not addTrack again (Firefox Internal Error).
    logAgentLocalSendersAfterAnswer(session, localStream);

    logWebRtcEvent('session.answered', {
      id: session.id,
      ...describeSessionMediaTracks(session)
    });
    startAgentIceMediaWatchdog(session, {
      callControlId: state.agentAnswerBridgeCallControlId || state.pendingAutoAnswerCallControlId,
      pcConfig: answerOptions.pcConfig || state.agentPeerConnectionConfig
    });
    scheduleAgentSdpInventoryLog(session);
    scheduleAgentRemoteAudioPlayback(remoteElementId, { attempts: 20, intervalMs: 1000 });
    return true;
  };

  const promise = run().finally(() => {
    state.agentSessionAnswerInFlight.delete(session);
  });
  state.agentSessionAnswerInFlight.set(session, promise);
  return promise;
}

function describeSessionMediaTracks(session) {
  const pc = session?.connection;
  const receivers = pc?.getReceivers?.() ?? [];
  const senders = pc?.getSenders?.() ?? [];
  return {
    receivers: receivers
      .filter((entry) => entry.track?.kind === 'audio')
      .map((entry) => ({
        muted: entry.track.muted,
        enabled: entry.track.enabled,
        readyState: entry.track.readyState
      })),
    senders: senders
      .filter((entry) => entry.track?.kind === 'audio')
      .map((entry) => ({
        muted: entry.track.muted,
        enabled: entry.track.enabled,
        readyState: entry.track.readyState
      }))
  };
}

const AGENT_SDP_INVENTORY_POLL_ATTEMPTS = 12;
const AGENT_SDP_INVENTORY_POLL_INTERVAL_MS = 250;

/**
 * Log the offer/answer SDP inventory once JsSIP populated the remote description.
 * Firefox-only ICE failures (stuck checking, zero RTP, Chrome fine) are decided by this
 * inventory — ice-lite, rtcp-mux, missing/private/mDNS candidates — which SIP-level logs
 * never surface. Values are summarized; no ufrag/pwd/fingerprint material.
 *
 * @param {import('jssip').RTCSession} session
 */
function scheduleAgentSdpInventoryLog(session) {
  let attempts = 0;
  const tick = () => {
    if (isRtcSessionDead(session)) return;
    let remote = null;
    let local = null;
    try {
      remote = summarizeSdp(session.connection?.remoteDescription?.sdp);
    } catch {
      remote = null;
    }
    try {
      local = summarizeSdp(session.connection?.localDescription?.sdp);
    } catch {
      local = null;
    }
    if (!remote) {
      if (attempts >= AGENT_SDP_INVENTORY_POLL_ATTEMPTS) {
        logWebRtcEvent('session.sdpInventoryUnavailable', { id: session.id });
        return;
      }
      attempts += 1;
      setTimeout(tick, AGENT_SDP_INVENTORY_POLL_INTERVAL_MS);
      return;
    }
    logWebRtcEvent('session.sdpInventory', {
      id: session.id,
      remote,
      local,
      remoteCompact: describeSdpSummary(remote),
      localCompact: describeSdpSummary(local)
    });
  };
  setTimeout(tick, 0);
}

/**
 * JsSIP attaches mediaStream tracks asynchronously after answer(). Only log senders —
 * never addTrack the same MediaStreamTrack (Firefox: "This track is already set on a sender"
 * → session.failed Internal Error → instant hangup).
 *
 * @param {import('jssip').RTCSession} session
 * @param {MediaStream} localStream
 */
function logAgentLocalSendersAfterAnswer(session, localStream) {
  const logOnce = (label) => {
    if (!session) return;
    const audioMuted = Boolean(session.isMuted?.().audio);
    localStream?.getAudioTracks?.().forEach((track) => {
      const audioTrack = track;
      // Same contract as collectAgentRtcAudioStats: never override an explicit mute.
      audioTrack.enabled = !audioMuted;
    });
    logWebRtcEvent('session.localSenders', {
      id: session.id,
      label,
      localTrackCount: localStream?.getAudioTracks?.().length ?? 0,
      ...describeSessionMediaTracks(session)
    });
  };

  logOnce('immediate');
  setTimeout(() => logOnce('+50ms'), 50);
  setTimeout(() => logOnce('+250ms'), 250);
}

function handleNewRtcSession(session) {
  const state = getVoiceAgentState();
  const { direction } = session;
  logWebRtcEvent('sdk.newRTCSession', { direction, id: session.id });

  if (direction === 'incoming') {
    // Do not release ICE prewarm here — applyAgentSessionAnswer waits then releases.
    session.on('progress', () => {
      logWebRtcEvent('session.progress', { id: session.id });
    });
    session.on('failed', (data) => {
      logWebRtcEvent('session.failed', {
        id: session.id,
        cause: data?.cause,
        message: data?.message
      });
      stopAgentIceMediaWatchdog(session);
      if (state.currentRtcSession === session) {
        state.currentRtcSession = null;
      }
      releaseRemoteAudioElement();
      emitSdkEvent({ type: 'failed', session, error: new Error(data?.cause || 'session failed') });
    });
    session.on('ended', (data) => {
      logWebRtcEvent('session.ended', {
        id: session.id,
        direction: 'incoming',
        originator: data?.originator,
        cause: data?.cause,
        message: data?.message
      });
      stopAgentIceMediaWatchdog(session);
      if (state.currentRtcSession === session) {
        state.currentRtcSession = null;
      }
      releaseRemoteAudioElement();
      emitSdkEvent({ type: 'ended', session });
    });
    if (state.pendingAutoAnswerCallControlId) {
      const callControlId = state.pendingAutoAnswerCallControlId;
      const ownedByAnswerBridge = state.agentAnswerBridgeCallControlId === callControlId;
      const remoteElementId = state.agentRemoteMediaElementId || VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID;
      attachRemoteAudio(session, remoteElementId);
      attachPeerConnectionDiagnostics(session);
      logWebRtcEvent('session.autoAnswerPending', {
        callControlId,
        id: session.id,
        ownedByAnswerBridge
      });
      state.pendingAutoAnswerCallControlId = null;
      applyAgentSessionAnswer(session).catch((error) => {
        logWebRtcEvent(
          ownedByAnswerBridge ? 'session.answerBridgeAutoAnswerFailed' : 'session.autoAnswerFailed',
          {
            id: session.id,
            message: error instanceof Error ? error.message : String(error)
          }
        );
      });
    }
    resolveAllPendingIncomingSessions(session);
    if (!state.currentRtcSession || isTerminalSessionState(state.currentRtcSession.status)) {
      state.currentRtcSession = session;
    }
    emitSdkEvent({ type: 'incoming', session });
    return;
  }

  if (state.pendingAutoAnswerCallControlId || state.agentAnswerBridgeCallControlId) {
    logWebRtcEvent('session.rejectOutgoingDuringPendingAnswer', {
      callControlId: state.pendingAutoAnswerCallControlId || state.agentAnswerBridgeCallControlId,
      id: session.id
    });
    try {
      session.terminate();
    } catch {
      // ignore
    }
    return;
  }

  if (!state.currentRtcSession || isTerminalSessionState(state.currentRtcSession.status)) {
    state.currentRtcSession = session;
    emitSdkEvent({ type: 'outgoing', session });
  }

  session.on('ended', (data) => {
    logWebRtcEvent('session.ended', {
      id: session.id,
      direction: session.direction,
      originator: data?.originator,
      cause: data?.cause,
      message: data?.message
    });
    stopAgentIceMediaWatchdog(session);
    if (state.currentRtcSession === session) {
      state.currentRtcSession = null;
    }
    emitSdkEvent({ type: 'ended', session });
  });
  session.on('failed', () => {
    stopAgentIceMediaWatchdog(session);
    if (state.currentRtcSession === session) {
      state.currentRtcSession = null;
    }
    emitSdkEvent({ type: 'failed', session });
  });
}

function clearAgentWebRtcReconnectTimer() {
  const state = getVoiceAgentState();
  if (state.reconnectTimerId != null) {
    clearTimeout(state.reconnectTimerId);
    state.reconnectTimerId = null;
  }
}

/**
 * @param {string} reason
 */
function scheduleAgentWebRtcReconnect(reason) {
  const state = getVoiceAgentState();
  if (state.intentionalTeardown) return;
  if (state.connectAgentWebRtcInFlight) return;
  if (state.reconnectTimerId != null) return;

  const attempt = state.reconnectAttempt;
  let delay = Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_BASE_DELAY_MS * (2 ** Math.min(attempt, 5))
  );
  if (reason === 'register-503') {
    delay = Math.max(delay, RECONNECT_503_MIN_DELAY_MS);
  }
  state.reconnectAttempt = attempt + 1;
  logWebRtcEvent('sdk.reconnectScheduled', {
    reason,
    delayMs: delay,
    attempt: state.reconnectAttempt
  });

  state.reconnectTimerId = setTimeout(() => {
    state.reconnectTimerId = null;
    if (state.intentionalTeardown) return;
    connectAgentWebRtcClient({ reason: 'auto-reconnect' })
      .then(() => {
        state.reconnectAttempt = 0;
      })
      .catch((error) => {
        logWebRtcEvent('sdk.reconnectFailed', {
          message: error instanceof Error ? error.message : String(error),
          attempt: state.reconnectAttempt
        });
        const failMessage = error instanceof Error ? error.message : String(error);
        scheduleAgentWebRtcReconnect(
          isSipMaximumCallsError(failMessage) ? 'register-503' : 'reconnect-failed'
        );
      });
  }, delay);
}

/**
 * Drop the current UA without stacking a second REGISTER on the same SIP user.
 * Unexpected transport loss → rotate tab_session_id so Nest/FS use a fresh agent_<tabId>.
 *
 * @param {{
 *   reason?: string,
 *   rotateTabSession?: boolean,
 *   waitUnregister?: boolean
 * }} [options]
 */
async function replaceAgentSipUserAgent(options = {}) {
  const state = getVoiceAgentState();
  const ua = state.sipUserAgent;
  state.sipUserAgent = null;
  state.agentClientReady = false;

  if (!ua) {
    if (options.rotateTabSession) {
      const nextTabSessionId = rotateVoiceTabSessionId();
      logWebRtcEvent('sdk.tabSessionRotated', {
        reason: options.reason || 'replace',
        tabSessionId: nextTabSessionId
      });
      refreshVoiceJoinRoom().catch(() => {});
    }
    return;
  }

  if (options.waitUnregister !== false && ua.isConnected?.() && ua.isRegistered?.()) {
    await teardownSipUserAgent(ua, { reason: options.reason || 'replace' });
  } else {
    try {
      ua.stop();
    } catch {
      // ignore
    }
    logWebRtcEvent('sdk.uaTeardown', {
      reason: options.reason || 'replace',
      unregisterOutcome: 'forced-stop'
    });
  }

  if (options.rotateTabSession) {
    const nextTabSessionId = rotateVoiceTabSessionId();
    logWebRtcEvent('sdk.tabSessionRotated', {
      reason: options.reason || 'replace',
      tabSessionId: nextTabSessionId
    });
    refreshVoiceJoinRoom().catch(() => {});
  }
}

function bindAgentWebRtcPageLifecycle() {
  const state = getVoiceAgentState();
  if (state.pageLifecycleBound || typeof window === 'undefined') return;
  state.pageLifecycleBound = true;

  const onPageHide = () => {
    state.intentionalTeardown = true;
    clearAgentWebRtcReconnectTimer();
    abortPendingIncomingAgentCallWait(AGENT_ANSWER_ABORTED);
    const ua = state.sipUserAgent;
    state.sipUserAgent = null;
    state.agentClientReady = false;
    if (!ua) return;
    // Best-effort: page may die before unregister completes.
    teardownSipUserAgent(ua, { reason: 'pagehide', timeoutMs: 800 }).catch(() => {});
  };

  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('beforeunload', onPageHide);
}

function waitForUaTransportConnected(ua, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (ua.isConnected?.()) {
      resolve(ua);
      return;
    }
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('SIP WebSocket transport connection timed out'));
    }, timeoutMs);

    const onConnected = () => {
      cleanup();
      resolve(ua);
    };
    const onDisconnected = () => {
      cleanup();
      reject(new Error('SIP WebSocket disconnected before transport connected'));
    };

    const cleanup = () => {
      clearTimeout(timer);
      ua.off('connected', onConnected);
      ua.off('disconnected', onDisconnected);
    };

    ua.on('connected', onConnected);
    ua.on('disconnected', onDisconnected);
  });
}

function waitForUaRegistered(ua, timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    if (ua.isRegistered()) {
      resolve(ua);
      return;
    }
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('FreeSWITCH agent SIP registration timed out'));
    }, timeoutMs);

    const onRegistered = () => {
      cleanup();
      resolve(ua);
    };
    const onFailed = (error) => {
      cleanup();
      const response = error?.response;
      const status = response?.status_code ?? response?.status;
      const reason = response?.reason_phrase ?? error?.cause;
      const detail = [status, reason].filter(Boolean).join(' ');
      const message = detail
        ? `SIP registration failed (${detail})`
        : 'SIP registration failed';
      reject(error instanceof Error ? error : new Error(message));
    };

    const cleanup = () => {
      clearTimeout(timer);
      ua.off('registered', onRegistered);
      ua.off('registrationFailed', onFailed);
    };

    ua.on('registered', onRegistered);
    ua.on('registrationFailed', onFailed);
  });
}

/**
 * @param {{ reason?: string }} [options]
 */
export async function connectAgentWebRtcClient(options = {}) {
  const state = getVoiceAgentState();
  bindAgentWebRtcPageLifecycle();

  if (state.disconnectAgentWebRtcInFlight) {
    await state.disconnectAgentWebRtcInFlight.catch(() => {});
  }

  if (state.connectAgentWebRtcInFlight) {
    return state.connectAgentWebRtcInFlight;
  }

  if (state.sipUserAgent?.isRegistered()) {
    state.intentionalTeardown = false;
    state.reconnectAttempt = 0;
    clearAgentWebRtcReconnectTimer();
    logWebRtcEvent('sdk.alreadyRegistered', {
      tabSessionId: getOrCreateVoiceTabSessionId(),
      agentClientReady: state.agentClientReady,
      reason: options.reason
    });
    return state.sipUserAgent;
  }

  state.intentionalTeardown = false;
  clearAgentWebRtcReconnectTimer();

  const tabSessionId = getOrCreateVoiceTabSessionId();
  state.connectAgentWebRtcInFlight = (async () => {
    logWebRtcEvent('sdk.connecting', { tabSessionId, reason: options.reason });

    if (state.sipUserAgent) {
      // Never stack a second UA on the same tab_session_id (causes "has N WSS contacts").
      await replaceAgentSipUserAgent({
        reason: 'pre-connect-replace',
        rotateTabSession: false,
        waitUnregister: true
      });
    }

    const connectTabSessionId = getOrCreateVoiceTabSessionId();

    let credentials;
    try {
      credentials = await VoiceCallsApiClient.fetchWebRtcCredentials(connectTabSessionId);
    } catch (error) {
      logWebRtcEvent('sdk.credentialsFailed', {
        tabSessionId: connectTabSessionId,
        message: error?.message ?? String(error),
        status: error?.status
      });
      throw error;
    }

    const { sip } = credentials;
    if (!sip?.wsUrl || !sip?.uri || !sip?.password) {
      logWebRtcEvent('sdk.invalidCredentials', {
        tabSessionId: connectTabSessionId,
        sip: sip
          ? {
            wsUrl: sip.wsUrl,
            uri: sip.uri,
            hasPassword: Boolean(sip.password)
          }
          : null
      });
      throw new Error('Invalid WebRTC credentials from voice-service');
    }

    state.agentPeerConnectionConfig = buildAgentPeerConnectionConfig(credentials);
    if (typeof credentials.remoteMediaElementId === 'string' && credentials.remoteMediaElementId.trim()) {
      state.agentRemoteMediaElementId = credentials.remoteMediaElementId.trim();
    }
    const turnUrls = (state.agentPeerConnectionConfig?.iceServers ?? [])
      .flatMap((entry) => (Array.isArray(entry.urls) ? entry.urls : [entry.urls]))
      .filter((url) => typeof url === 'string' && url.startsWith('turn'));
    logWebRtcEvent('sdk.credentials', {
      tabSessionId: connectTabSessionId,
      wsUrl: sip.wsUrl,
      uri: sip.uri,
      viaTransport: sip.viaTransport ?? sip.via_transport,
      iceTransportPolicy: state.agentPeerConnectionConfig?.iceTransportPolicy,
      turnConfigured: turnUrls.length > 0,
      turnHasCredential: (state.agentPeerConnectionConfig?.iceServers ?? []).some(
        (entry) => typeof entry.credential === 'string' && entry.credential.length > 0
      )
    });
    if (!turnUrls.length) {
      logWebRtcWarn(
        'No TURN in credentials — agent audio may fail when FreeSWITCH is on a VPS (check voice-service VOICE_WEBRTC_ICE_SERVERS / GET /voice/webrtc/credentials).'
      );
    }

    // Re-check after await: another connect may have registered meanwhile.
    if (state.sipUserAgent?.isRegistered()) {
      logWebRtcEvent('sdk.reuseRegisteredUa', { tabSessionId: connectTabSessionId });
      return state.sipUserAgent;
    }
    if (state.sipUserAgent) {
      await replaceAgentSipUserAgent({
        reason: 'pre-start-replace',
        rotateTabSession: false,
        waitUnregister: true
      });
    }

    JsSIP.debug.disable();
    const socket = new JsSIP.WebSocketInterface(sip.wsUrl);
    const viaTransport = (typeof sip.viaTransport === 'string' && sip.viaTransport.trim())
      || (typeof sip.via_transport === 'string' && sip.via_transport.trim())
      || '';
    if (viaTransport) {
      socket.via_transport = viaTransport.trim().toUpperCase();
    } else if (
      typeof sip.wsUrl === 'string'
      && /ngrok\.(dev|io)|ngrok-free\.app/i.test(sip.wsUrl)
    ) {
      socket.via_transport = 'WS';
    }
    const uaConfig = buildJsSipUaConfiguration(sip, connectTabSessionId, socket);
    logWebRtcEvent('sdk.uaConfig', {
      tabSessionId: connectTabSessionId,
      registrar: uaConfig.registrar_server,
      contactUri: uaConfig.contact_uri,
      realm: uaConfig.realm,
      viaTransport: socket.via_transport
    });
    const ua = new JsSIP.UA(uaConfig);
    state.sipUserAgent = ua;

    ua.on('newRTCSession', (data) => {
      handleNewRtcSession(data.session);
    });
    ua.on('connected', () => {
      logWebRtcEvent('sdk.transportConnected', { tabSessionId: connectTabSessionId });
    });
    ua.on('disconnected', (event) => {
      if (state.sipUserAgent !== ua) {
        return;
      }
      // Capture before clearing — only rotate when we abandon a live REGISTER (zombie risk).
      const hadLiveRegistration = state.agentClientReady || Boolean(ua.isRegistered?.());
      state.agentClientReady = false;
      logWebRtcEvent('sdk.disconnected', {
        tabSessionId: connectTabSessionId,
        intentional: state.intentionalTeardown,
        hadLiveRegistration,
        ...summarizeDisconnectEvent(event)
      });
      emitSdkEvent({ type: 'disconnected' });

      if (state.intentionalTeardown) {
        return;
      }

      // Transport already dead → cannot unregister. Rotate only if this AOR was registered
      // (avoid creating agent_* storm on Connection Error before first 200 OK).
      replaceAgentSipUserAgent({
        reason: 'sdk.disconnected',
        rotateTabSession: hadLiveRegistration,
        waitUnregister: false
      }).finally(() => {
        scheduleAgentWebRtcReconnect(
          hadLiveRegistration ? 'sdk.disconnected' : 'register-failed'
        );
      });
    });
    ua.on('registrationFailed', (error) => {
      const response = error?.response;
      logWebRtcEvent('sdk.registrationFailed', {
        tabSessionId: connectTabSessionId,
        cause: error?.cause,
        status: response?.status_code ?? response?.status,
        reason: response?.reason_phrase
      });
    });

    ua.start();
    try {
      await waitForUaTransportConnected(ua);
      await waitForUaRegistered(ua);
    } catch (error) {
      const message = error?.message ?? String(error);
      logWebRtcEvent('sdk.registerTimeout', {
        tabSessionId: connectTabSessionId,
        message
      });
      if (state.sipUserAgent === ua) {
        const rotateTabSession = shouldRotateTabSessionAfterRegisterFailure(message);
        await replaceAgentSipUserAgent({
          reason: 'register-failed',
          rotateTabSession,
          // Transport may still be up on 503 — try clean unregister to free the AOR slot.
          waitUnregister: isSipMaximumCallsError(message)
        });
        if (!rotateTabSession) {
          logWebRtcEvent('sdk.tabSessionKept', {
            reason: isSipMaximumCallsError(message)
              ? 'sip_503_maximum_calls'
              : 'register_failed_no_rotate',
            tabSessionId: connectTabSessionId
          });
        }
      }
      if (!state.intentionalTeardown) {
        scheduleAgentWebRtcReconnect(
          isSipMaximumCallsError(message) ? 'register-503' : 'register-failed'
        );
      }
      throw error;
    }
    if (state.sipUserAgent !== ua) {
      try {
        ua.stop();
      } catch {
        // superseded
      }
      return state.sipUserAgent;
    }
    logWebRtcEvent('sdk.registered', { uri: sip.uri, tabSessionId: connectTabSessionId });
    state.agentClientReady = true;
    state.reconnectAttempt = 0;
    logWebRtcEvent('sdk.ready', { uri: sip.uri, tabSessionId: connectTabSessionId });
    emitSdkEvent({ type: 'ready' });
    if (state.agentPeerConnectionConfig) {
      state.agentIcePrewarmPromise = prewarmAgentIceTransport(state.agentPeerConnectionConfig)
        .then((relayOk) => {
          // Prewarm may use relay-only probes; do NOT mutate answer pcConfig away from credentials.
          logWebRtcEvent('sdk.icePrewarmDone', {
            tabSessionId: connectTabSessionId,
            relayOk,
            answerIceTransportPolicy: state.agentPeerConnectionConfig?.iceTransportPolicy,
            answerIceServers: summarizeIceServersForLog(state.agentPeerConnectionConfig?.iceServers)
          });
          return relayOk;
        })
        .catch((error) => {
          logWebRtcEvent('sdk.icePrewarmBackgroundFailed', {
            tabSessionId: connectTabSessionId,
            message: error instanceof Error ? error.message : String(error)
          });
          return false;
        });
    } else {
      state.agentIcePrewarmPromise = null;
    }
    return ua;
  })();

  try {
    return await state.connectAgentWebRtcInFlight;
  } finally {
    state.connectAgentWebRtcInFlight = null;
  }
}

export async function disconnectAgentWebRtcClient() {
  const state = getVoiceAgentState();
  if (state.disconnectAgentWebRtcInFlight) {
    return state.disconnectAgentWebRtcInFlight;
  }

  state.disconnectAgentWebRtcInFlight = (async () => {
    state.intentionalTeardown = true;
    clearAgentWebRtcReconnectTimer();
    state.reconnectAttempt = 0;

    if (state.currentRtcSession) {
      try {
        state.currentRtcSession.terminate();
      } catch {
        // ignore
      }
      state.currentRtcSession = null;
    }

    const ua = state.sipUserAgent;
    state.sipUserAgent = null;
    state.agentClientReady = false;
    if (ua) {
      await teardownSipUserAgent(ua, { reason: 'disconnect' });
    }

    state.agentPeerConnectionConfig = undefined;
    state.agentIcePrewarmPromise = null;
    state.agentRemoteMediaElementId = VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID;
    releaseAgentLocalAudioCapture();
    state.pendingAutoAnswerCallControlId = null;
    state.agentAnswerBridgeCallControlId = null;
    abortPendingIncomingAgentCallWait();
    resetRemoteAudioAttachmentTracking();
  })();

  try {
    return await state.disconnectAgentWebRtcInFlight;
  } finally {
    state.disconnectAgentWebRtcInFlight = null;
  }
}

/**
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<import('jssip').RTCSession>}
 */
export function waitForIncomingAgentCall(options = {}) {
  const state = getVoiceAgentState();
  const timeoutMs = options.timeoutMs ?? 35000;

  if (
    state.currentRtcSession?.direction === 'incoming'
    && !isRtcSessionDead(state.currentRtcSession)
    && !isAgentSessionAlreadyAnswered(state.currentRtcSession)
  ) {
    return Promise.resolve(state.currentRtcSession);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finishResolve = (session) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      state.pendingIncomingResolvers.delete(waiter);
      unsubscribe();
      resolve(session);
    };
    const finishReject = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      state.pendingIncomingResolvers.delete(waiter);
      unsubscribe();
      reject(error);
    };

    const timer = setTimeout(() => {
      finishReject(new Error('Timed out waiting for agent WebRTC leg'));
    }, timeoutMs);

    const waiter = { resolve: finishResolve, reject: finishReject };
    state.pendingIncomingResolvers.add(waiter);

    const unsubscribe = subscribeAgentSdkEvents((event) => {
      if (event.type === 'incoming' && event.session) {
        finishResolve(event.session);
      }
      if (event.type === 'failed' && event.error) {
        finishReject(event.error);
      }
    });
  });
}

/**
 * @param {{ incomingCallPromise?: Promise<import('jssip').RTCSession>, timeoutMs?: number }} [options]
 */
export async function answerCurrentAgentCall(options = {}) {
  const state = getVoiceAgentState();
  const session = options.incomingCallPromise
    ? await options.incomingCallPromise
    : await waitForIncomingAgentCall({ timeoutMs: options.timeoutMs ?? 45000 });

  if (!session || isTerminalSessionState(session.status)) {
    throw new Error('No active agent WebRTC session to answer');
  }

  const remoteElementId = state.agentRemoteMediaElementId || VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID;

  if (isAgentSessionAlreadyAnswered(session)) {
    scheduleAgentRemoteAudioPlayback(remoteElementId, {
      attempts: 20,
      intervalMs: 1000
    });
    try {
      await waitForAgentSessionConfirmed(session, options.timeoutMs ?? 60_000);
    } catch (error) {
      logWebRtcEvent('session.confirmFailed', {
        id: session.id,
        message: error instanceof Error ? error.message : String(error),
        alreadyAnswered: true
      });
    }
    syncAgentRemoteAudioPlayback(remoteElementId);
    return session;
  }

  await applyAgentSessionAnswer(session);
  try {
    await waitForAgentSessionConfirmed(session, options.timeoutMs ?? 60_000);
  } catch (error) {
    logWebRtcEvent('session.confirmFailed', {
      id: session.id,
      message: error instanceof Error ? error.message : String(error)
    });
    scheduleAgentRemoteAudioPlayback(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    return session;
  }
  syncAgentRemoteAudioPlayback(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
  return session;
}

/**
 * @param {import('jssip').RTCSession} session
 * @param {number} [timeoutMs]
 */
export function waitForAgentSessionConfirmed(session, timeoutMs = 25000) {
  if (session.isEstablished?.() || session.status === 'confirmed') {
    return Promise.resolve(session);
  }
  if (isPeerConnectionMediaReady(session)) {
    logWebRtcEvent('session.confirmed', { id: session.id, via: 'pc-ready' });
    return Promise.resolve(session);
  }
  if (isPeerConnectionIceReady(session) && canTreatAgentIceAsPostAnswer(session)) {
    logWebRtcEvent('session.confirmed', { id: session.id, via: 'ice-after-answer' });
    return Promise.resolve(session);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('WebRTC session did not reach confirmed state'));
    }, timeoutMs);

    const finishOk = (via) => {
      if (settled) return;
      settled = true;
      cleanup();
      logWebRtcEvent('session.confirmed', { id: session.id, via });
      resolve(session);
    };
    const finishErr = (message) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    const onConfirmed = () => finishOk('event');
    const onEnded = () => finishErr('WebRTC session ended before confirm');
    const onFailed = () => finishErr('WebRTC session failed before confirm');

    const onPcState = () => {
      const pc = session.connection;
      if (isPeerConnectionDead(pc)) {
        finishErr('WebRTC peer connection closed');
        return;
      }
      if (pc?.iceConnectionState === 'failed' || pc?.connectionState === 'failed') {
        finishErr('WebRTC ICE connection failed (use TURN relay; check UDP 49152-65535 on NSG)');
        return;
      }
      if (isPeerConnectionMediaReady(session)) {
        finishOk('pc-ready');
        return;
      }
      if (isPeerConnectionIceReady(session) && canTreatAgentIceAsPostAnswer(session)) {
        finishOk('ice-after-answer');
      }
    };

    const cleanup = () => {
      clearTimeout(timer);
      session.off('confirmed', onConfirmed);
      session.off('ended', onEnded);
      session.off('failed', onFailed);
      const pc = session.connection;
      if (pc) {
        pc.removeEventListener('connectionstatechange', onPcState);
        pc.removeEventListener('iceconnectionstatechange', onPcState);
      }
    };

    session.on('confirmed', onConfirmed);
    session.on('ended', onEnded);
    session.on('failed', onFailed);

    const pc = session.connection;
    if (pc) {
      pc.addEventListener('connectionstatechange', onPcState);
      pc.addEventListener('iceconnectionstatechange', onPcState);
    }
    session.on('peerconnection', (data) => {
      const peer = data.peerconnection;
      if (peer) {
        peer.addEventListener('connectionstatechange', onPcState);
        peer.addEventListener('iceconnectionstatechange', onPcState);
      }
    });
    onPcState();
  });
}

export async function hangupCurrentAgentCall() {
  const state = getVoiceAgentState();
  abortPendingIncomingAgentCallWait();
  const session = state.currentRtcSession;
  if (session) {
    stopAgentIceMediaWatchdog(session);
  }
  releaseRemoteAudioElement();
  if (!session) {
    releaseAgentLocalAudioCapture();
    return;
  }
  if (isTerminalSessionState(session.status)) {
    state.currentRtcSession = null;
    releaseAgentLocalAudioCapture();
    return;
  }
  try {
    session.terminate();
  } catch {
    // Session may already be ended (e.g. FS leg INCOMPATIBLE_DESTINATION).
  }
  state.currentRtcSession = null;
  releaseAgentLocalAudioCapture();
}

export function muteCurrentAgentCall() {
  const session = getVoiceAgentState().currentRtcSession;
  if (!session || isTerminalSessionState(session.status)) return false;
  if (session.isMuted?.().audio) return true;
  session.mute({ audio: true });
  return true;
}

export function unmuteCurrentAgentCall() {
  const session = getVoiceAgentState().currentRtcSession;
  if (!session || isTerminalSessionState(session.status)) return false;
  if (!session.isMuted?.().audio) return true;
  session.unmute({ audio: true });
  return true;
}

export function isCurrentAgentCallMuted() {
  const session = getVoiceAgentState().currentRtcSession;
  if (!session) return false;
  return Boolean(session.isMuted?.().audio);
}

export function releaseAgentCallOnSdkTerminal(session) {
  const state = getVoiceAgentState();
  if (session) {
    stopAgentIceMediaWatchdog(session);
  }
  if (state.currentRtcSession === session) {
    state.currentRtcSession = null;
  }
  if (session) {
    state.agentSessionAnswerInitiated.delete(session);
  }
  releaseRemoteAudioElement();
  releaseAgentLocalAudioCapture();
}
